import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Button, DatePicker, Form, Modal, Select, Spin } from 'antd'
import PropTypes from 'prop-types'
import { createReportIntent } from 'services/reports/reportsService'
import { errorMessage, successMessage } from 'services/messager'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
	getDocumentsByStatus,
	getDocumentTemplates,
} from 'services/documents/documentsServices'

const { RangePicker } = DatePicker

const toIsoDate = (value) => (value ? value.format('YYYY-MM-DD') : null)

const fetchModels = async () => {
	try {
		const data = await getDocumentTemplates({
			page: 1,
			limit: 100,
		})
		return data.items
	} catch (error) {
		errorMessage({ content: 'Erro ao obter modelos' })
	}
}

const fetchWorkflowSteps = async () => {
	try {
		const data = await getDocumentsByStatus()

		return data
	} catch (error) {
		errorMessage({ content: 'Erro ao obter etapas do workflow' })
	}
}

const ReportModal = ({ showModal, handleCancel }) => {
	const history = useHistory()
	const queryClient = useQueryClient()
	const [form] = Form.useForm()
	const [submitting, setSubmitting] = useState(false)

	const { data: models, isLoading: isLoadingModels } = useQuery({
		queryKey: ['models-options'],
		queryFn: fetchModels,
	})

	const { data: workflows, isLoading: isLoadingWorkflows } = useQuery({
		queryKey: ['workflow-options'],
		queryFn: fetchWorkflowSteps,
	})

	const workflowSteps =
		!isLoadingWorkflows && workflows
			? Object.keys(workflows)
					?.filter((statusName) => workflows[statusName].length > 0)
					.map((statusName) => ({ value: statusName, label: statusName }))
			: []

	const templateOptions =
		!isLoadingModels && models
			? [
					{ value: '', label: 'Todos' },
					...models.map((t) => ({
						value: t.id,
						label: t.name,
					})),
				]
			: []
	const workflowStepOptions =
		!isLoadingWorkflows && workflowSteps
			? [{ value: '', label: 'Todos' }, ...workflowSteps]
			: []

	const handleClose = () => {
		form.resetFields()
		handleCancel()
	}

	const handleSubmit = async () => {
		const values = await form.validateFields()
		const [contractStartFrom, contractStartTo] = values.contractStart || []
		const [contractEndFrom, contractEndTo] = values.contractEnd || []

		const filters = {
			template_id: values.templateId || null,
			current_step: values.currentStep || null,
			contract_start_from: toIsoDate(contractStartFrom),
			contract_start_to: toIsoDate(contractStartTo),
			contract_end_from: toIsoDate(contractEndFrom),
			contract_end_to: toIsoDate(contractEndTo),
		}

		const cleanFilters = Object.fromEntries(
			Object.entries(filters).filter(([, v]) => v !== null && v !== '')
		)

		try {
			setSubmitting(true)
			await createReportIntent({
				reportType: 'documents',
				filters: cleanFilters,
			})
			handleClose()
			successMessage({
				content:
					'Relatório solicitado com sucesso! O download ficará disponível assim que o processamento for concluído.',
			})
			queryClient.invalidateQueries({ key: 'reports' })
			history.push('/reports')
		} catch (error) {
			const status = error?.response?.status
			if (status === 403) {
				errorMessage({
					content: 'Você não tem permissão para gerar relatórios',
				})
			} else {
				errorMessage({ content: 'Erro ao solicitar relatório' })
			}
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<Modal
			open={showModal}
			onCancel={handleClose}
			title="Gerar Relatório de Documentos"
			destroyOnClose
			footer={[
				<Button key="cancel" onClick={handleClose} disabled={submitting}>
					Cancelar
				</Button>,
				<Button
					key="submit"
					type="primary"
					onClick={handleSubmit}
					loading={submitting}>
					Gerar
				</Button>,
			]}>
			<Form form={form} layout="vertical" id="newReportForm">
				<Form.Item label="Modelo" name="templateId">
					<Select
						allowClear
						showSearch
						placeholder="Todos os modelos"
						optionFilterProp="label"
						loading={isLoadingModels}
						notFoundContent={isLoadingModels ? <Spin size="small" /> : null}
						options={templateOptions}
					/>
				</Form.Item>
				<Form.Item label="Etapa do Workflow" name="currentStep">
					<Select
						allowClear
						showSearch
						placeholder="Todas as etapas"
						optionFilterProp="label"
						loading={isLoadingWorkflows}
						notFoundContent={isLoadingWorkflows ? <Spin size="small" /> : null}
						options={workflowStepOptions}
					/>
				</Form.Item>
				<Form.Item label="Data de início do contrato" name="contractStart">
					<RangePicker
						format="DD/MM/YYYY"
						style={{ width: '100%' }}
						placeholder={['Data inicial', 'Data final']}
					/>
				</Form.Item>
				<Form.Item label="Data de vencimento do contrato" name="contractEnd">
					<RangePicker
						format="DD/MM/YYYY"
						style={{ width: '100%' }}
						placeholder={['Data inicial', 'Data final']}
					/>
				</Form.Item>
			</Form>
		</Modal>
	)
}

ReportModal.propTypes = {
	showModal: PropTypes.bool,
	handleCancel: PropTypes.func.isRequired,
}

export default ReportModal
